/**
 * useBatchAirportChange — Timetable 批量换机场预览管线。
 *
 * 本 hook 承载批量换机场/切换线路的航路解析、字段派生和自动计算预览拼装，让
 * TimetableSection 只负责装配预览 UI 与确认入口。
 */

import { useCallback, useState } from "react";
import { message } from "antd";

import { BATCH_AUTO_CALCULATE_OPTIONS, buildBatchPreviewFromChangedFlights } from "../lib/autoCalculatePreviewPipeline";
import { BATCH_CHANGE_AIRPORT, BATCH_SWITCH_LINE } from "../lib/editCommand";
import { getModifiedStatus, getSelectedLocalFlights } from "../lib/flightDataUtils";
import { applyRecalculationRules } from "../lib/flightRecalculationRules";
import { normalizeAirportCode, normalizeRouteList } from "../lib/routeUtils";
import { scheduleDraftReferenceDataService } from "../services/scheduleDraftReferenceDataService";

const EMPTY_LOCAL_FLIGHTS_MAP = new Map();

const BATCH_CHANGE_AIRPORT_FIELDS = [
    "departure_airport",
    "arrival_airport",
    "flight_route_id",
    "route_snapshot",
    "flight_route_name",
    "flight_route_block_time_minutes",
    "block_time_minutes",
    "min_block_time_minutes",
    "sta",
    "flight_date",
    "day_of_week",
    "change_status",
    "change_source",
];

const AIRPORT_CODE_PATTERN = /^[A-Z]{3}$/;

function showBatchRecalculationWarnings(warnings = []) {
    const normalizedWarnings = warnings.filter(Boolean).map((warning) => String(warning));
    if (!normalizedWarnings.length) {
        return;
    }

    const uniqueWarnings = Array.from(new Set(normalizedWarnings));
    const summary = uniqueWarnings.slice(0, 3).join("；");
    const suffix = uniqueWarnings.length > 3 ? "；..." : "";
    const prefix = normalizedWarnings.length > 1 ? `批量操作产生 ${normalizedWarnings.length} 条提示：` : "";
    message.warning(`${prefix}${summary}${suffix}`);
}

function getRouteLegKey(departureAirport, arrivalAirport) {
    return `${departureAirport}:${arrivalAirport}`;
}

function getFlightRouteLegKey(flight) {
    return getRouteLegKey(normalizeAirportCode(flight?.departure_airport), normalizeAirportCode(flight?.arrival_airport));
}

function isValidAirportCode(value) {
    return AIRPORT_CODE_PATTERN.test(value);
}

function normalizeBatchRouteStrategy(routeStrategy) {
    if (routeStrategy === "recent") {
        // TODO: implement recent-route selection from a frontend cache; fallback to the default matcher for now.
        return "default";
    }

    return "default";
}

function getBatchAirportRouteLegs(selectedFlights, columnKey, normalizedAirport) {
    const routeLegs = new Map();

    selectedFlights.forEach((flight) => {
        const departureAirport = columnKey === "departure_airport" ? normalizedAirport : normalizeAirportCode(flight.departure_airport);
        const arrivalAirport = columnKey === "arrival_airport" ? normalizedAirport : normalizeAirportCode(flight.arrival_airport);

        if (!departureAirport || !arrivalAirport) {
            return;
        }

        const legKey = getRouteLegKey(departureAirport, arrivalAirport);
        routeLegs.set(legKey, { departureAirport, arrivalAirport });
    });

    return routeLegs;
}

function normalizeRouteSwitchMappings(mappings = []) {
    const normalizedMappings = new Map();

    (Array.isArray(mappings) ? mappings : []).forEach((mapping) => {
        const currentDepartureAirport = normalizeAirportCode(mapping?.currentDepartureAirport);
        const currentArrivalAirport = normalizeAirportCode(mapping?.currentArrivalAirport);
        const targetDepartureAirport = normalizeAirportCode(mapping?.targetDepartureAirport);
        const targetArrivalAirport = normalizeAirportCode(mapping?.targetArrivalAirport);
        const hasTargetDepartureAirport = targetDepartureAirport !== "";
        const hasTargetArrivalAirport = targetArrivalAirport !== "";
        const effectiveDepartureAirport = hasTargetDepartureAirport ? targetDepartureAirport : currentDepartureAirport;
        const effectiveArrivalAirport = hasTargetArrivalAirport ? targetArrivalAirport : currentArrivalAirport;
        const departureAirportChanges = hasTargetDepartureAirport && targetDepartureAirport !== currentDepartureAirport;
        const arrivalAirportChanges = hasTargetArrivalAirport && targetArrivalAirport !== currentArrivalAirport;

        normalizedMappings.set(getRouteLegKey(currentDepartureAirport, currentArrivalAirport), {
            currentDepartureAirport,
            currentArrivalAirport,
            targetDepartureAirport,
            targetArrivalAirport,
            hasTargetDepartureAirport,
            hasTargetArrivalAirport,
            departureAirportChanges,
            arrivalAirportChanges,
            hasAnyTarget: hasTargetDepartureAirport || hasTargetArrivalAirport,
            effectiveDepartureAirport,
            effectiveArrivalAirport,
            hasEffectiveChange: departureAirportChanges || arrivalAirportChanges,
        });
    });

    return normalizedMappings;
}

function isInvalidRouteSwitchMapping(mapping) {
    if (!mapping?.hasAnyTarget) {
        return false;
    }

    return (mapping.hasTargetDepartureAirport && !isValidAirportCode(mapping.targetDepartureAirport))
        || (mapping.hasTargetArrivalAirport && !isValidAirportCode(mapping.targetArrivalAirport))
        || !isValidAirportCode(mapping.effectiveDepartureAirport)
        || !isValidAirportCode(mapping.effectiveArrivalAirport);
}

function isEffectiveRouteSwitchMapping(mapping) {
    return Boolean(mapping?.hasEffectiveChange && !isInvalidRouteSwitchMapping(mapping));
}

function getRouteSwitchRecalculationField(flight, target) {
    if (target.effectiveDepartureAirport !== normalizeAirportCode(flight?.departure_airport)) {
        return "departure_airport";
    }

    return "arrival_airport";
}

function getBatchRouteSwitchRouteLegs(selectedFlights, switchMappings) {
    const routeLegs = new Map();

    selectedFlights.forEach((flight) => {
        const target = switchMappings.get(getFlightRouteLegKey(flight));
        if (!isEffectiveRouteSwitchMapping(target)) {
            return;
        }

        const legKey = getRouteLegKey(target.effectiveDepartureAirport, target.effectiveArrivalAirport);
        routeLegs.set(legKey, {
            departureAirport: target.effectiveDepartureAirport,
            arrivalAirport: target.effectiveArrivalAirport,
        });
    });

    return routeLegs;
}

function addRouteReference(routeReferenceData, route) {
    if (!route) {
        return;
    }

    const routeId = route.id == null ? null : String(route.id);
    if (routeId && !routeReferenceData.seenRouteIds.has(routeId)) {
        routeReferenceData.seenRouteIds.add(routeId);
        routeReferenceData.routes.push(route);
    }
    if (routeId) {
        routeReferenceData.routesById[route.id] = route;
    }
}

async function loadBatchRouteReferenceData(routeLegs, routeStrategy) {
    const effectiveRouteStrategy = normalizeBatchRouteStrategy(routeStrategy);
    const routeReferenceData = {
        routes: [],
        routesById: {},
        routesByLeg: {},
        routeStrategy: effectiveRouteStrategy,
        seenRouteIds: new Set(),
    };

    try {
        await Promise.all(Array.from(routeLegs.entries()).map(async ([legKey, { departureAirport, arrivalAirport }]) => {
            const routeOptions = await scheduleDraftReferenceDataService.getRoutesByLeg(departureAirport, arrivalAirport);
            const routes = normalizeRouteList(routeOptions);

            routeReferenceData.routesByLeg[legKey] = routes;
            routes.forEach((route) => addRouteReference(routeReferenceData, route));
        }));
    } catch (error) {
        throw new Error(error.response?.data?.detail || error.message || "");
    }

    delete routeReferenceData.seenRouteIds;
    return routeReferenceData;
}

export function useBatchAirportChange({
    draftId,
    localFlightsMap,
    disabledReason,
    airportConstraintsMap = {},
    autoCalculateOptions = BATCH_AUTO_CALCULATE_OPTIONS,
    clearPreview,
    setPendingPreview,
    setAutoCalculatePreview,
}) {
    const [isProcessing, setIsProcessing] = useState(false);
    const effectiveLocalFlightsMap = localFlightsMap || EMPTY_LOCAL_FLIGHTS_MAP;

    const requestBatchPreview = useCallback(
        async ({ changedFlightsById, fields, type, label, previewTitle, noChangeMessage, errorMessage }) => {
            if (!draftId || disabledReason) {
                if (disabledReason) {
                    message.warning(disabledReason);
                }
                return null;
            }

            try {
                const preview = await buildBatchPreviewFromChangedFlights({
                    draftId,
                    localFlightsMap: effectiveLocalFlightsMap,
                    changedFlightsById,
                    fields,
                    type,
                    label,
                    options: autoCalculateOptions,
                    airportConstraintsMap,
                    beforeCalculate: clearPreview,
                });

                if (!preview) {
                    message.warning("未找到可计算的航班链");
                    return null;
                }

                if (!preview.changeSet.changes.length) {
                    setAutoCalculatePreview(null);
                    message.info(noChangeMessage || `${label}未产生变更`);
                    return null;
                }

                const nextPreview = {
                    ...preview,
                    previewTitle,
                };
                setAutoCalculatePreview(nextPreview);
                setPendingPreview?.(preview.changeSet);
                return nextPreview;
            } catch (error) {
                message.error(error.response?.data?.detail || error.message || errorMessage || `${label}失败`);
                return null;
            }
        },
        [airportConstraintsMap, autoCalculateOptions, clearPreview, disabledReason, draftId, effectiveLocalFlightsMap, setAutoCalculatePreview, setPendingPreview],
    );

    const handleBatchChangeAirport = useCallback(
        async ({ flightIds, columnKey, airport, routeStrategy }) => {
            if (isProcessing) {
                message.warning("自动计算请求处理中");
                return null;
            }
            if (disabledReason) {
                message.warning(disabledReason);
                return null;
            }

            const normalizedAirport = normalizeAirportCode(airport);
            if (!isValidAirportCode(normalizedAirport)) {
                message.warning("请输入机场三字码");
                return null;
            }

            const selectedFlights = getSelectedLocalFlights(effectiveLocalFlightsMap, flightIds);
            if (!selectedFlights.length) {
                message.warning("请选择需要换机场的航班");
                return null;
            }

            setIsProcessing(true);
            try {
                const routeLegs = getBatchAirportRouteLegs(selectedFlights, columnKey, normalizedAirport);
                const routeReferenceData = await loadBatchRouteReferenceData(routeLegs, routeStrategy);
                const recalculationWarnings = [];
                const changedFlights = selectedFlights.map((flight) => {
                    const departureAirport = columnKey === "departure_airport" ? normalizedAirport : normalizeAirportCode(flight.departure_airport);
                    const arrivalAirport = columnKey === "arrival_airport" ? normalizedAirport : normalizeAirportCode(flight.arrival_airport);
                    const baseFlight = {
                        ...flight,
                        departure_airport: departureAirport,
                        arrival_airport: arrivalAirport,
                    };
                    const { patches, warnings } = applyRecalculationRules(columnKey, {
                        newValue: normalizedAirport,
                        currentFlight: baseFlight,
                        referenceData: routeReferenceData,
                    });
                    recalculationWarnings.push(...warnings);

                    return {
                        ...baseFlight,
                        ...patches,
                        change_status: getModifiedStatus(flight),
                        change_source: "USER",
                    };
                });
                const changedFlightsById = new Map(changedFlights.map((flight) => [String(flight.id), flight]));

                const preview = await requestBatchPreview({
                    changedFlightsById,
                    fields: BATCH_CHANGE_AIRPORT_FIELDS,
                    type: BATCH_CHANGE_AIRPORT,
                    label: `${columnKey === "departure_airport" ? "批量换出发机场" : "批量换到达机场"} ${normalizedAirport}`,
                    previewTitle: "换机场预览",
                    noChangeMessage: "换机场未产生变更",
                    errorMessage: "换机场失败",
                });
                if (preview) {
                    showBatchRecalculationWarnings(recalculationWarnings);
                }
                return preview;
            } catch (error) {
                message.error(`航路数据加载失败，已取消批量换机场${error.message ? `：${error.message}` : ""}`);
                return null;
            } finally {
                setIsProcessing(false);
            }
        },
        [disabledReason, effectiveLocalFlightsMap, isProcessing, requestBatchPreview],
    );

    const handleBatchSwitchLine = useCallback(
        async ({ flightIds, mappings }) => {
            if (isProcessing) {
                message.warning("自动计算请求处理中");
                return null;
            }
            if (disabledReason) {
                message.warning(disabledReason);
                return null;
            }

            const selectedFlights = getSelectedLocalFlights(effectiveLocalFlightsMap, flightIds);
            if (selectedFlights.length < 2) {
                message.warning("请选择至少 2 个需要切换线路的航班");
                return null;
            }

            const switchMappings = normalizeRouteSwitchMappings(mappings);
            if (!switchMappings.size) {
                message.warning("至少填写一个与原机场不同的目标机场");
                return null;
            }

            const invalidMapping = Array.from(switchMappings.values()).find(isInvalidRouteSwitchMapping);
            if (invalidMapping) {
                message.warning("已填写的目标机场需为三字码");
                return null;
            }

            const hasEffectiveMapping = selectedFlights.some((flight) => isEffectiveRouteSwitchMapping(switchMappings.get(getFlightRouteLegKey(flight))));
            if (!hasEffectiveMapping) {
                message.warning("至少填写一个与原机场不同的目标机场");
                return null;
            }

            setIsProcessing(true);
            try {
                const routeLegs = getBatchRouteSwitchRouteLegs(selectedFlights, switchMappings);
                const routeReferenceData = await loadBatchRouteReferenceData(routeLegs, "default");
                const recalculationWarnings = [];
                const changedFlights = selectedFlights.map((flight) => {
                    const target = switchMappings.get(getFlightRouteLegKey(flight));
                    if (!isEffectiveRouteSwitchMapping(target)) {
                        return null;
                    }

                    const recalculationField = getRouteSwitchRecalculationField(flight, target);
                    const baseFlight = {
                        ...flight,
                        departure_airport: target.departureAirportChanges ? target.targetDepartureAirport : flight.departure_airport,
                        arrival_airport: target.arrivalAirportChanges ? target.targetArrivalAirport : flight.arrival_airport,
                    };
                    const { patches, warnings } = applyRecalculationRules(recalculationField, {
                        newValue: baseFlight[recalculationField],
                        previousValue: flight[recalculationField],
                        currentFlight: baseFlight,
                        referenceData: routeReferenceData,
                    });
                    recalculationWarnings.push(...warnings);

                    return {
                        ...baseFlight,
                        ...patches,
                        change_status: getModifiedStatus(flight),
                        change_source: "USER",
                    };
                }).filter(Boolean);
                const changedFlightsById = new Map(changedFlights.map((flight) => [String(flight.id), flight]));
                const changedLegCount = routeLegs.size;

                const preview = await requestBatchPreview({
                    changedFlightsById,
                    fields: BATCH_CHANGE_AIRPORT_FIELDS,
                    type: BATCH_SWITCH_LINE,
                    label: `切换线路 ${selectedFlights.length} 个航班 / ${changedLegCount} 组航段`,
                    previewTitle: "切换线路预览",
                    noChangeMessage: "切换线路未产生变更",
                    errorMessage: "切换线路失败",
                });
                if (preview) {
                    showBatchRecalculationWarnings(recalculationWarnings);
                }
                return preview;
            } catch (error) {
                message.error(`航路数据加载失败，已取消切换线路${error.message ? `：${error.message}` : ""}`);
                return null;
            } finally {
                setIsProcessing(false);
            }
        },
        [disabledReason, effectiveLocalFlightsMap, isProcessing, requestBatchPreview],
    );

    return {
        handleBatchChangeAirport,
        handleBatchSwitchLine,
        isProcessing,
    };
}

export default useBatchAirportChange;
